import { useCallback, useMemo } from 'react'
import { fbt } from 'fbtee'
import { toast } from 'sonner'
import { useArtistInterests } from '../../hooks/useArtistInterests'
import { useEventArtistInterests } from '../../hooks/useEventArtistInterests'
import type { useGuestGuard } from '../../hooks/useGuestGuard'
import { interestPrompt } from '../../lib/guestPrompts'
import { track } from '../../lib/analytics'
import type { LineupEntry } from '../../types'
import { followedNotPicked } from './LineupView/followedArtists'

const EMPTY_SET: ReadonlySet<string> = new Set()
// runMutation already toasted the failure.
const noop = () => {}

/**
 * The viewer's hearts on this event's lineup, plus the artists they follow
 * app-wide but haven't hearted here — the lineup's rose-outlined hearts and its
 * "Add the artists you follow" CTA.
 */
export function useLineupArtistInterests(
  eventId: string,
  uid: string | null,
  ensureInterested: () => void,
  guard: ReturnType<typeof useGuestGuard>['guard'],
  lineup: LineupEntry[],
  cancelled: boolean,
) {
  const { interestedArtistIds: followedArtistIds, loading: followsLoading } = useArtistInterests(uid)
  const { interestedArtistIds, loading: picksLoading, toggleArtistInterest, markArtists } =
    useEventArtistInterests(uid, eventId, { followedArtistIds, onMarked: ensureInterested })

  // Empty until both lists settle, so no outline flashes on an artist about to read as hearted.
  const followHintIds = useMemo(() => {
    if (followsLoading || picksLoading || followedArtistIds.size === 0) return EMPTY_SET
    return new Set([...followedArtistIds].filter(id => !interestedArtistIds.has(id)))
  }, [followsLoading, picksLoading, followedArtistIds, interestedArtistIds])

  const followCtaArtistIds = useMemo(
    () => (cancelled ? [] : followedNotPicked(lineup, followHintIds)),
    [cancelled, lineup, followHintIds],
  )

  const addFollowed = useCallback(async () => {
    const added = await markArtists(
      followCtaArtistIds,
      fbt("Couldn't add your artists", 'Error toast when adding the artists the user follows to an event lineup fails'),
    )
    if (added === 0) return
    track('followed_artists_added', { event_id: eventId, artist_count: added })
    toast.success(fbt(
      'Added ' + fbt.plural('artist', added, { showCount: 'yes', many: 'artists' }),
      'Toast after hearting, for this event, the lineup artists the user already follows',
    ))
  }, [markArtists, followCtaArtistIds, eventId])

  const prompt = interestPrompt('artist_interest')
  return {
    interestedArtistIds,
    followHintIds,
    followCtaArtistIds,
    guardedToggleArtistInterest: guard(toggleArtistInterest, prompt),
    // An act's heart: one prompt for a guest, one batch for everyone else.
    guardedMarkArtists: guard((ids: string[]) => { markArtists(ids).catch(noop) }, prompt),
    addFollowedArtists: guard(() => { addFollowed().catch(noop) }, prompt),
  }
}
